import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { leadOptionLabel } from '@/modules/leads/leadApi';
import type { CrmDataProvider } from '@/providers/types';
import type { Lead } from '@/types';

type CrmLeadInputProps = {
    value: Lead | null;
    onChange: (lead: Lead | null) => void;
    /** When set, preloads the lead once (e.g. quote edit hydration). */
    initialLeadId?: number | null;
};

export function CrmLeadInput({ value, onChange, initialLeadId }: CrmLeadInputProps) {
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (value) {
            setInputValue(leadOptionLabel(value));
        }
    }, [value]);

    useEffect(() => {
        if (initialLeadId == null || value != null) return;
        let cancelled = false;
        dataProvider.getLead(initialLeadId)
            .then((lead: Lead) => {
                if (!cancelled) {
                    onChange(lead);
                    setInputValue(leadOptionLabel(lead));
                }
            })
            .catch(() => {
                /* optional lead — ignore load failure */
            });
        return () => {
            cancelled = true;
        };
    }, [initialLeadId, value, dataProvider]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        dataProvider.searchLeads(inputValue)
            .then((opts: Lead[]) => {
                if (!cancelled) setOptions(opts);
            })
            .catch(() => {
                if (!cancelled) setOptions([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [inputValue, dataProvider]);

    return (
        <Autocomplete
            options={options}
            value={value}
            inputValue={inputValue}
            loading={loading}
            getOptionLabel={leadOptionLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onInputChange={(_, v) => setInputValue(v)}
            onChange={(_, v) => onChange(v)}
            renderInput={params => (
                <TextField
                    {...params}
                    label="CRM Lead (optional)"
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
